import { connect } from 'react-redux';
import React from 'react';
import { Button, Input } from 'antd';
import { t } from 'i18next';
import { ReceiverColumns, REJECTED, APPROVED, PENDING } from '../../constants/ReceiverConstants';
import { ApplicationState } from '../../reducers/index';
import { AsyncDispatch } from '../../../types/global';
import {
  ExtendedResources,
  StandardResource,
  StandardResourceCodeDescription,
} from '../../../types/resources';
import { PriceSheetType } from '../../../types/price_sheet';
import {
  Receiver,
  ReceiverDetails,
  ReceiverRequest,
  SKUListParams,
  SKUItem,
  SKUPaginationInfo,
  DashboardData,
} from '../../../types/receiver';
import { UserType } from '../../../types/user';
import { mapResourcesToReceivers } from '../../selectors/receiver/receiverSelector';
import { withContainerWrapper } from '../ContainerWrapper';
import ReceiverTable from '../../components/body/receiver/ReceiverTable';
import ReceiverDetailsModal from '../../components/body/receiver/ReceiverDetailsModal';
import ReceiverStatsDrawer from '../../components/body/receiver/ReceiverStatsDrawer';
import {
  fetchReceiverAdoptionStats,
  refreshReceiverAdoptionStats,
  fetchReceiverSkus,
  checkJobStatus as checkAdoptionJobStatus,
} from '../../actions/receiver/adoption';
import {
  fetchReceiverScorecardStats,
  refreshReceiverScorecardStats,
  searchReceiverScorecardPartNumbers,
  checkJobStatus,
} from '../../actions/receiver/scorecard';
import actions from '../../actions/receiver';
import { fetchPriceSheets } from '../../actions/parent/price_sheets/fetch';
import { fetchParentCustomFields } from '../../actions/parent/custom_fields/fetch';
import { isManufacturer } from '../../utils/UserUtils';
import translations from '../../constants/ReceiverTranslation.json';
import { intercomEvent } from '../../utils/IntercomUtils';
import { fetchReceiver } from '../../actions/receiver/fetch';
import { getOrderStringToInt } from '../../utils/Utils';
import { hasPermission } from '../../utils/Permissions';
import { receiverBulkInvite, receiverBulkInviteByFile } from '../../actions/receiver/update';
import { fetchAllAccountFilters } from '../../actions/catalogue/filter/fetch';
import { Brand } from '../../../types/brand';
import { activeBrandsWithoutOe } from '../../selectors/brand/brandSelector';
import ReceiverBulkInviteModal from '../../components/body/receiver/ReceiverBulkInviteModal';
import { BrandCustomField, CustomFieldIdName } from '../../../types/custom_fields';
import { ScorecardData, JobStatus } from '../../../types/scorecard';
import {
  PartNumberSearchParams,
  PartNumberPaginationInfo,
} from '../../components/body/receiver/scorecard/PartNumberBreakdown';

type ReceiverContainerProps = {
  user: UserType;
  isLoading: boolean;
  isLoadingFullList: boolean;
  fetchingReceiverDetails: boolean;
  receivers: Receiver[];
  fullReceiverList: Receiver[];
  receiverDetails?: ReceiverDetails;
  reqStatuses: StandardResourceCodeDescription[];
  priceSheets: PriceSheetType[];
  priceTypes: ExtendedResources[];
  dispatch: AsyncDispatch;
  activeBrands: Brand[];
  canManageResellerInBulk: boolean;
  interchangeQuality: StandardResource[];
  fetchingCustomFields: boolean;
  customFields: BrandCustomField[];
  customFieldsIdNameList: CustomFieldIdName[];
  adoptionData: { [receiverId: number]: DashboardData };
  adoptionLoading: { [receiverId: number]: boolean };
  skuData: { [receiverId: number]: SKUItem[] };
  skuPagination: { [receiverId: number]: SKUPaginationInfo };
  skuLoading: { [receiverId: number]: boolean };
  skuSearchError: { [receiverId: number]: string | null };
  scorecardData: { [receiverId: number]: ScorecardData };
  scorecardLoading: { [receiverId: number]: boolean };
  partNumberPagination: { [receiverId: number]: PartNumberPaginationInfo };
  partNumberLoading: { [receiverId: number]: boolean };
  refreshJobs: { [receiverId: number]: JobStatus | null };
  adoptionRefreshJobs: { [receiverId: number]: JobStatus | null };
  isStatsView?: boolean;
};

class ReceiverContainer extends React.Component<ReceiverContainerProps> {
  state = {
    filterKeywords: '',
    showReceiverDetailModal: false,
    showReceiverBulkInviteModal: false,
    drawerVisible: false,
    selectedReceiver: null as Receiver | null,
  };

  componentDidMount() {
    const { dispatch, customFieldsIdNameList } = this.props;

    this.props.dispatch(actions.fetchFullReceiverList());
    this.props.dispatch(fetchAllAccountFilters());
    if (isManufacturer(this.props.user)) {
      this.props.dispatch(actions.fetchReceiverRequestCount());
    }
    this.props.dispatch(fetchPriceSheets());
    if (!customFieldsIdNameList || customFieldsIdNameList.length === 0) {
      dispatch(fetchParentCustomFields());
    }
    intercomEvent(this.props.isStatsView ? 'viewed-receiver-stats' : 'viewed-receiver');
  }

  getReceiverStatus = (receiver?: Receiver) => {
    const { fullReceiverList, receiverDetails, isStatsView } = this.props;
    const targetReceiver = isStatsView
      ? receiver
      : fullReceiverList.find(r => r.receiver_id === receiverDetails?.id);

    if (!targetReceiver) return {};

    const approved = !!targetReceiver?.requests.find(
      req => this.getRequestStatus(req) === APPROVED
    );
    const pending = !!targetReceiver?.requests.find(req => this.getRequestStatus(req) === PENDING);
    const rejected = !!targetReceiver?.requests.find(
      req => this.getRequestStatus(req) === REJECTED
    );
    const authorizedByReceiver = !!targetReceiver?.requests.find(
      req => !!req.authorized_by_receiver_at && this.getRequestStatus(req) === PENDING
    );
    return {
      approved,
      pending,
      rejected,
      authorizedByReceiver,
    };
  };

  getRequestStatus = (request: ReceiverRequest) => {
    return request.status || '';
  };

  handleShowReceiverDetails = (receiverId: number) => {
    const receiver = this.props.receivers.find(r => r.receiver_id === receiverId);

    this.props.dispatch(actions.fetchReceiverDetails(receiverId));
    this.props.dispatch(actions.fetchReceiverWarehouseList());
    this.setState({ showReceiverDetailModal: true });

    intercomEvent('viewed-receiver', {
      action: 'viewed-profile',
      receiver: receiver?.name || '',
    });
  };

  handleShowReceiverDrawer = (receiverId: number) => {
    const receiver = this.props.receivers.find(r => r.receiver_id === receiverId);

    if (receiver) {
      this.props.dispatch(actions.fetchReceiverDetails(receiverId));
      this.props.dispatch(actions.fetchReceiverWarehouseList());
      this.setState({
        drawerVisible: true,
        selectedReceiver: receiver,
      });

      // Fetch adoption data for the selected receiver
      this.fetchAdoptionData(receiverId);

      // Fetch scorecard data for the selected receiver
      this.fetchScorecardData(receiverId);

      intercomEvent('viewed-receiver-stats', {
        action: 'opened-drawer',
        receiver: receiver?.name || '',
      });
    }
  };

  handleCloseDrawer = () => {
    this.setState({
      drawerVisible: false,
      selectedReceiver: null,
    });
  };

  fetchAdoptionData = async (receiverId: number) => {
    // Dispatch action - reducer will handle loading state and data storage
    this.props.dispatch(fetchReceiverAdoptionStats(receiverId));
  };

  handleRefreshAdoption = async () => {
    const { selectedReceiver } = this.state;
    const receiverId = selectedReceiver?.receiver_id;

    if (!receiverId || this.props.adoptionLoading[receiverId]) {
      return;
    }

    // Use the refresh action - it will trigger the refresh and then fetch updated data
    this.props.dispatch(refreshReceiverAdoptionStats(receiverId));
  };

  handleFetchSkus = async (receiverId: number, params: SKUListParams) => {
    return this.props.dispatch(fetchReceiverSkus(receiverId, params));
  };

  fetchScorecardData = async (receiverId: number) => {
    // Dispatch action - reducer will handle loading state and data storage
    this.props.dispatch(fetchReceiverScorecardStats(receiverId));
  };

  handleRefreshScorecard = async () => {
    const { selectedReceiver } = this.state;
    const receiverId = selectedReceiver?.receiver_id;

    if (!receiverId || this.props.scorecardLoading[receiverId]) {
      return;
    }

    // Use the refresh action - it will trigger the refresh and then fetch updated data
    this.props.dispatch(refreshReceiverScorecardStats(receiverId));
  };

  handleFetchScorecardData = async (receiverId: number) => {
    return this.props.dispatch(fetchReceiverScorecardStats(receiverId));
  };

  handleSearchPartNumbers = async (receiverId: number, params: PartNumberSearchParams) => {
    return this.props.dispatch(searchReceiverScorecardPartNumbers(receiverId, params));
  };

  handleCheckJobStatus = (jobId: string) => {
    return this.props.dispatch(checkJobStatus(jobId));
  };

  handleCheckAdoptionJobStatus = (jobId: string) => {
    return this.props.dispatch(checkAdoptionJobStatus(jobId));
  };

  requestReceiverAccess = ({
    receiverId,
    brandIds,
    integrationTypeId,
    selectedPriceSheetIds,
    selectedPriceTypeIds,
    withoutPrices,
    filterIds,
    withoutInterchanges,
    interchangeQualityIds,
    isNewRequest,
    withoutCustomFields,
    customFieldIds,
  }: {
    receiverId: number;
    brandIds: number[];
    integrationTypeId: number | null;
    selectedPriceSheetIds: number[];
    selectedPriceTypeIds: number[];
    withoutPrices: boolean;
    filterIds: number[];
    withoutInterchanges: boolean;
    interchangeQualityIds: number[];
    isNewRequest: boolean;
    withoutCustomFields: boolean;
    customFieldIds: number[];
  }) => {
    const { receivers, activeBrands } = this.props;

    this.props.dispatch(
      actions.createReceiverRequest({
        receiverId,
        brandIds,
        integrationTypeId,
        selectedPriceSheetIds,
        selectedPriceTypeIds,
        withoutPrices,
        filterIds,
        withoutInterchanges,
        interchangeQualityIds,
        withoutCustomFields,
        customFieldIds,
      })
    );

    if (isNewRequest) {
      intercomEvent(this.props.isStatsView ? 'viewed-receiver-stats' : 'viewed-receiver', {
        action: 'requested-receiver',
        receiver: receivers.find(r => r.receiver_id === receiverId)?.name || '',
        brand_names: brandIds.map(id => activeBrands.find(b => b.id === id)?.name).join(', '),
      });
    }
  };

  approveReceivers = ({
    receiverId,
    brandIds,
    selectedPriceSheetIds,
    selectedPriceTypeIds,
    withoutPrices,
    filterIds,
    withoutInterchanges,
    interchangeQualityIds,
    withoutCustomFields,
    customFieldIds,
  }: {
    receiverId: number;
    brandIds: number[];
    selectedPriceSheetIds: number[];
    selectedPriceTypeIds: number[];
    withoutPrices: boolean;
    filterIds: number[];
    withoutInterchanges: boolean;
    interchangeQualityIds: number[];
    withoutCustomFields: boolean;
    customFieldIds: number[];
  }) => {
    const { receivers, activeBrands } = this.props;
    const receiver = receivers.find(r => r.receiver_id === receiverId);

    this.props.dispatch(
      actions.approveReceivers({
        receiverId,
        brandIds,
        selectedPriceSheetIds,
        selectedPriceTypeIds,
        withoutPrices,
        filterIds,
        withoutInterchanges,
        interchangeQualityIds,
        withoutCustomFields,
        customFieldIds,
      })
    );
    intercomEvent(this.props.isStatsView ? 'viewed-receiver-stats' : 'viewed-receiver', {
      action: 'approved-receiver',
      receiver: receiver?.name || '',
      brand_names: brandIds.map(id => activeBrands.find(b => b.id === id)?.name).join(', '),
    });
  };

  declineReceivers = (receiverId: number, brandIds: number[], reason?: string) => {
    const { receivers, activeBrands } = this.props;
    const receiver = receivers.find(r => r.receiver_id === receiverId);

    this.props.dispatch(actions.declineReceivers({ receiverId, brandIds, reason }));
    intercomEvent(this.props.isStatsView ? 'viewed-receiver-stats' : 'viewed-receiver', {
      action: 'rejected-receiver',
      receiver: receiver?.name || '',
      brand_names: brandIds.map(id => activeBrands.find(b => b.id === id)?.name).join(', '),
    });
  };

  cancelRequests = (receiverId: number, brandIds: number[]) => {
    this.props.dispatch(actions.cancelRequests({ receiverId, brandIds }));
  };

  handleReceiverFilter = (filterReceiverIds: number[], statusIds: number[], sortOrder: string) => {
    this.props.dispatch(
      fetchReceiver({
        orderCondition: sortOrder ? ReceiverColumns.COMPANY : undefined,
        filterReceiverIds,
        statusIds,
        descending: getOrderStringToInt(sortOrder),
      })
    );
  };

  handleBulkInvite = async ({ values, file }: { values?: any[]; file?: any }) => {
    const { dispatch, user } = this.props;

    let bulkResponse;

    if (values) bulkResponse = await dispatch(receiverBulkInvite(values));
    else bulkResponse = await dispatch(receiverBulkInviteByFile(file));

    dispatch(actions.fetchFullReceiverList());

    intercomEvent('reseller-list-uploaded', {
      resellerCount: values?.length.toString() || '',
      user: `${user.first_name} ${user.last_name}`,
      companyName: user.account_name,
    });

    return bulkResponse;
  };

  render() {
    const {
      isLoadingFullList,
      isLoading,
      fetchingReceiverDetails,
      receivers,
      fullReceiverList,
      reqStatuses,
      receiverDetails,
      user,
      activeBrands,
      canManageResellerInBulk,
      isStatsView = false,
    } = this.props;
    const {
      showReceiverDetailModal,
      showReceiverBulkInviteModal,
      filterKeywords,
      drawerVisible,
      selectedReceiver,
    } = this.state;
    const canAccessReceiverProfile = hasPermission(user, 'can_access_receiver_profile');
    const canAccessReceiverStats = hasPermission(user, 'has_receiver_stats_access');

    // Get adoption data for selected receiver
    const selectedReceiverId = selectedReceiver?.receiver_id;
    const adoptionData = selectedReceiverId
      ? this.props.adoptionData[selectedReceiverId]
      : undefined;
    const adoptionLoading = selectedReceiverId
      ? this.props.adoptionLoading[selectedReceiverId] || false
      : false;

    // Get part number pagination for selected receiver
    const partNumberPagination = selectedReceiverId
      ? this.props.partNumberPagination[selectedReceiverId]
      : undefined;
    const partNumberLoading = selectedReceiverId
      ? this.props.partNumberLoading[selectedReceiverId] || false
      : false;

    // Get refresh job status for selected receiver
    const refreshJobStatus = selectedReceiverId ? this.props.refreshJobs[selectedReceiverId] : null;

    // Get adoption refresh job status for selected receiver
    const adoptionRefreshJobStatus = selectedReceiverId
      ? this.props.adoptionRefreshJobs[selectedReceiverId]
      : null;

    // Show access denied for stats view if user doesn't have permission
    if (isStatsView && !canAccessReceiverStats) {
      return (
        <div className="page-layout receiver-stats-container">
          <div className="page-layout__content flex items-center justify-center">
            <div className="text-center">
              <h3 className="text-lg font-medium mb-2">{t('receiver:accessDenied')}</h3>
              <p className="text-gray-600">{t('receiver:noPermissionReceiverStats')}</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className={`page-layout ${isStatsView ? 'receiver-stats-container' : 'receiver-container'}`}
      >
        <div className="page-layout__top-bar">
          <div className="page-layout__top-bar__container">
            <Input.Search
              className="page-layout__top-bar__search"
              value={this.state.filterKeywords}
              onChange={e => this.setState({ filterKeywords: e.target.value || '' })}
              placeholder={translations.searchPlaceholder}
              allowClear
            />
            {canManageResellerInBulk && (
              <Button
                onClick={() => this.setState({ showReceiverBulkInviteModal: true })}
                type="primary"
              >
                {t('receiver:inviteReseller')}
              </Button>
            )}
          </div>
        </div>
        <div className="page-layout__content flex">
          {isLoadingFullList ? (
            <div className="loader" />
          ) : (
            <ReceiverTable
              isLoading={isLoading}
              receivers={
                canAccessReceiverProfile && filterKeywords
                  ? receivers.filter(
                      ({ name }) =>
                        name && name.toLowerCase().includes(filterKeywords.toLowerCase())
                    )
                  : receivers
              }
              requestReceiverAccess={this.requestReceiverAccess}
              approveReceivers={this.approveReceivers}
              declineReceivers={this.declineReceivers}
              cancelRequests={this.cancelRequests}
              priceSheets={this.props.priceSheets}
              priceTypes={this.props.priceTypes}
              handleReceiverFilter={this.handleReceiverFilter}
              handleShowReceiverDetails={
                canAccessReceiverStats ? undefined : this.handleShowReceiverDetails
              }
              handleShowReceiverDrawer={
                canAccessReceiverStats ? this.handleShowReceiverDrawer : undefined
              }
              fullReceiverList={fullReceiverList}
              reqStatuses={reqStatuses}
              interchangeQuality={this.props.interchangeQuality}
              customFields={this.props.customFields}
              fetchingCustomFields={this.props.fetchingCustomFields}
              customFieldsIdNameList={this.props.customFieldsIdNameList}
              noRowExpanding={canAccessReceiverStats}
            />
          )}

          {canAccessReceiverStats && (
            <ReceiverStatsDrawer
              visible={drawerVisible}
              receiver={selectedReceiver}
              receiverDetails={receiverDetails}
              fetchingReceiverDetails={fetchingReceiverDetails}
              status={selectedReceiver ? this.getReceiverStatus(selectedReceiver) : {}}
              onClose={this.handleCloseDrawer}
              requestReceiverAccess={this.requestReceiverAccess}
              approveReceivers={this.approveReceivers}
              declineReceivers={this.declineReceivers}
              cancelRequests={this.cancelRequests}
              priceSheets={this.props.priceSheets}
              priceTypes={this.props.priceTypes}
              interchangeQuality={this.props.interchangeQuality}
              customFields={this.props.customFields}
              fetchingCustomFields={this.props.fetchingCustomFields}
              customFieldsIdNameList={this.props.customFieldsIdNameList}
              adoptionData={adoptionData}
              adoptionLoading={adoptionLoading}
              onRefreshAdoption={this.handleRefreshAdoption}
              onFetchAdoptionStats={this.fetchAdoptionData}
              onFetchSkus={this.handleFetchSkus}
              skuData={this.props.skuData}
              skuPagination={this.props.skuPagination}
              skuLoading={this.props.skuLoading}
              skuSearchError={this.props.skuSearchError}
              scorecardData={this.props.scorecardData}
              scorecardLoading={this.props.scorecardLoading}
              onRefreshScorecard={this.handleRefreshScorecard}
              onFetchScorecardData={this.handleFetchScorecardData}
              onSearchPartNumbers={this.handleSearchPartNumbers}
              partNumberPagination={partNumberPagination}
              partNumberLoading={partNumberLoading}
              refreshJobStatus={refreshJobStatus}
              onCheckJobStatus={this.handleCheckJobStatus}
              adoptionRefreshJobStatus={adoptionRefreshJobStatus}
              onCheckAdoptionJobStatus={this.handleCheckAdoptionJobStatus}
            />
          )}

          {canAccessReceiverProfile && (
            <ReceiverDetailsModal
              showModal={showReceiverDetailModal}
              receiverDetails={receiverDetails}
              fetchingReceiverDetails={fetchingReceiverDetails}
              status={showReceiverDetailModal ? this.getReceiverStatus() : {}}
              onCancel={() => this.setState({ showReceiverDetailModal: false })}
            />
          )}
          <ReceiverBulkInviteModal
            showModal={showReceiverBulkInviteModal}
            handleBulkInvite={this.handleBulkInvite}
            activeBrands={activeBrands}
            onClose={() => this.setState({ showReceiverBulkInviteModal: false })}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: ApplicationState) => {
  return {
    user: state.user.user,
    isLoading: state.receiver.receivers.isLoading,
    isLoadingFullList: state.receiver.receivers.isLoadingFullList,
    fetchingReceiverDetails: state.receiver.receivers.fetchingReceiverDetails,
    receivers: mapResourcesToReceivers(state),
    fullReceiverList: state.receiver.receivers.fullReceiverList,
    receiverDetails: state.receiver.receivers.receiverDetails,
    priceSheets: state.parent.priceSheets.priceSheets || [],
    priceTypes: (state.resources.data.price && state.resources.data.price.types) || [],
    reqStatuses: state.resources.data.receiver?.request_statuses || [],
    interchangeQuality:
      state.resources.data.interchange_information?.extended_qualitity_grade_levels || [],
    activeBrands: activeBrandsWithoutOe(state),
    canManageResellerInBulk: hasPermission(state.user.user, 'can_manage_reseller_in_bulk'),
    customFields: state.brand.customFields.customFields,
    fetchingCustomFields: state.brand.customFields.fetchingCustomFields,
    customFieldsIdNameList: state.parent.parentCustomFields.customFieldsIdNameList,
    adoptionData: state.receiver.receivers.adoptionData,
    adoptionLoading: state.receiver.receivers.adoptionLoading,
    skuData: state.receiver.receivers.skuData,
    skuPagination: state.receiver.receivers.skuPagination,
    skuLoading: state.receiver.receivers.skuLoading,
    skuSearchError: state.receiver.receivers.skuSearchError,
    scorecardData: state.receiver.receivers.scorecardData,
    scorecardLoading: state.receiver.receivers.scorecardLoading,
    partNumberPagination: state.receiver.receivers.partNumberPagination,
    partNumberLoading: state.receiver.receivers.partNumberLoading,
    refreshJobs: state.receiver.receivers.refreshJobs,
    adoptionRefreshJobs: state.receiver.receivers.adoptionRefreshJobs,
  };
};

export default connect(mapStateToProps)(withContainerWrapper(ReceiverContainer));
